import { AxiosResponse } from 'axios';
import { queryClient } from 'src/config/react-query';
import {
  DateToRequest,
  Role,
  VacationsFilterValues,
} from 'src/types/vacations';

export const vacationsKeys = {
  all: () => ['vacations'],
  issuerList: (name: string) => [...vacationsKeys.all(), name],
  policyTypes: {
    all: () => [...vacationsKeys.all(), 'policyTypes'],
    list: () => [...vacationsKeys.policyTypes.all(), 'list'],
    listByUserId: (id: number | string) => [
      ...vacationsKeys.policyTypes.all(),
      Number(id),
    ],
    detail: (policyTypeId: number | string, userId: number | string | null) => [
      ...vacationsKeys.policyTypes.list(),
      Number(userId),
      Number(policyTypeId),
    ],
    balance: (
      policyTypeId: number | string,
      userId: number | string | null,
    ) => [...vacationsKeys.policyTypes.detail(policyTypeId, userId), 'balance'],
    conformation: (
      policyTypeId: number | string,
      userId: number | string | null,
    ) => [
      ...vacationsKeys.policyTypes.balance(policyTypeId, userId),
      'conformation',
    ],
    requestedDaysAmount: (
      policyTypeId: number | string,
      userId: number | string | null,
      fromDate: DateToRequest,
      toDate: DateToRequest,
    ) => [
      ...vacationsKeys.policyTypes.detail(policyTypeId, userId),
      fromDate,
      toDate,
      'requestedDaysAmount',
    ],
  },
  collaborator: {
    all: () => [...vacationsKeys.all(), 'collaborator'],
    requestsReport: {
      all: () => [...vacationsKeys.collaborator.all(), 'requestsReport'],
      filters: (policyTypeId: string | number) => [
        ...vacationsKeys.collaborator.requestsReport.all(),
        Number(policyTypeId),
      ],
    },
  },
  manager: {
    all: () => [...vacationsKeys.all(), 'manager'],
    requestCalendar: {
      all: () => [...vacationsKeys.manager.all(), 'requestCalendar'],
      filters: (month: Date, filter: VacationsFilterValues) => [
        ...vacationsKeys.manager.requestCalendar.all(),
        month.toString(),
        filter,
      ],
    },
    requestsReport: {
      all: () => [...vacationsKeys.manager.all(), 'requestsReport'],
      filters: (filter: VacationsFilterValues) => [
        ...vacationsKeys.manager.requestsReport.all(),
        filter,
      ],
    },
    balanceReport: {
      all: () => [...vacationsKeys.manager.all(), 'balanceReport'],
      filters: (filter: VacationsFilterValues) => [
        ...vacationsKeys.manager.balanceReport.all(),
        filter,
      ],
    },
  },
  policies: (policyTypes: number[]) => [
    ...vacationsKeys.all(),
    'policies',
    policyTypes,
  ],
  detail: (id: string | number) => [
    ...vacationsKeys.all(),
    'detail',
    Number(id),
  ],
  approvers: (vacationId: string | number, stepId: string | number) => [
    ...vacationsKeys.detail(vacationId),
    'step',
    Number(stepId),
    'approvers',
  ],
  steps: (id: string | number) => [...vacationsKeys.detail(id), 'steps'],
  notifications: {
    all: () => [...vacationsKeys.all(), 'notifications'],
    module: () => [...vacationsKeys.notifications.all(), 'module'],
  },
};

export const updateBalanceReport = () => {
  queryClient.invalidateQueries(vacationsKeys.manager.balanceReport.all());
};

export const updateRequestsCollaborator = () => {
  queryClient.invalidateQueries(
    vacationsKeys.collaborator.requestsReport.all(),
  );
};

export const updateRequestsManager = () => {
  queryClient.invalidateQueries(vacationsKeys.manager.requestsReport.all());
};

export const updateVacations = id => {
  queryClient.invalidateQueries(vacationsKeys.detail(id));
};

export const getListPolicyTypes = (userId: string | number): AxiosResponse =>
  queryClient.getQueryData(vacationsKeys.policyTypes.listByUserId(userId));

export const getPolicyTypeById = (id, userId) => {
  const { data } = getListPolicyTypes(userId);
  return data?.find(elem => elem.id === id);
};

export const updateNotifications = role => {
  queryClient.setQueryData(
    vacationsKeys.notifications.module(),
    (oldData: any): any => {
      if (!oldData) return oldData;
      if (role === Role.MANAGER) {
        oldData.vacationRequestsAsManagerNotifications = 0;
      } else {
        oldData.vacationRequestsAsCollaboratorNotifications = 0;
      }

      return oldData;
    },
  );
};

export const updateRedBubblesManager = () => {
  queryClient.setQueryData(
    vacationsKeys.notifications.module(),
    (oldData: any): any => {
      if (!oldData) return oldData;
      oldData.vacationRequestsAsManagerNotifications =
        oldData.vacationRequestsAsManagerNotifications - 1;
      return oldData;
    },
  );
};

export const updateAllRequestInCalendar = () => {
  queryClient.invalidateQueries(vacationsKeys.manager.requestCalendar.all());
};

export const restoreMonthWithFirstPage = (
  month: Date,
  filters: VacationsFilterValues,
) => {
  queryClient.setQueryData(
    vacationsKeys.manager.requestCalendar.filters(month, filters),
    (oldData: any): any => {
      if (!oldData) return oldData;
      return {
        pages: oldData.pages.slice(0, 1),
        pageParams: oldData.pageParams.slice(0, 1),
      };
    },
  );
};
